const longOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short'
};

const shortOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
};

const compactOptions: Intl.DateTimeFormatOptions = {
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
};

/**
 * Parse timestamps returned by our API.
 *
 * The backend frequently returns ISO strings without an explicit timezone. In those cases,
 * we treat them as UTC (by appending "Z") to match how the rest of the app displays dates.
 */
export const parseApiDate = (dateInput: string | Date | null | undefined): Date | null => {
    if (!dateInput) {
        return null;
    }

    if (dateInput instanceof Date) {
        return Number.isNaN(dateInput.getTime()) ? null : dateInput;
    }

    const dateString = dateInput.trim();
    if (!dateString) {
        return null;
    }

    // If the timestamp already includes an explicit timezone ("Z" or an offset),
    // let the Date constructor handle it as-is.
    const parsedDate = (
        dateString.endsWith('Z') || dateString.includes('+') || dateString.includes('-', 10)
    )
        ? new Date(dateString)
        : new Date(`${dateString}Z`);

    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

export const formatDate = (dateString: string | Date | null | undefined, type: 'short' | 'long' | 'compact' = 'long') => {
    const date = parseApiDate(dateString);
    if (!date) {
        return '';
    }

    const optionsMap = { short: shortOptions, long: longOptions, compact: compactOptions };
    return new Intl.DateTimeFormat('en-US', optionsMap[type]).format(date);
};

export const addMinutes = (dateString: string | Date | null | undefined, minutes: number, type: 'short' | 'long' = 'long') => {
    const date = parseApiDate(dateString);
    if (!date) {
        return '';
    }

    date.setUTCMinutes(date.getUTCMinutes() + minutes);
    const options = type === 'short' ? shortOptions : longOptions;
    return new Intl.DateTimeFormat('en-US', options).format(date);
};

/**
 * Format a timestamp as a relative time string (e.g., "2 hours ago")
 * @param timestamp - The timestamp to format
 * @returns A formatted relative time string
 */
export const formatRelativeTime = (timestamp: string | undefined): string => {
    if (!timestamp) return '';
    
    const now = new Date();
    const date = new Date(timestamp);
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    // Handle invalid dates
    if (isNaN(seconds)) return '';
    
    // Time intervals in seconds
    const intervals = {
        year: 31536000,
        month: 2592000,
        week: 604800,
        day: 86400,
        hour: 3600,
        minute: 60
    };
    
    // Check each interval
    if (seconds < 60) {
        return 'just now';
    } else if (seconds < 3600) {
        const minutes = Math.floor(seconds / 60);
        return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
    } else if (seconds < 86400) {
        const hours = Math.floor(seconds / 3600);
        return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
    } else if (seconds < 604800) {
        const days = Math.floor(seconds / 86400);
        return `${days} ${days === 1 ? 'day' : 'days'} ago`;
    } else if (seconds < 2592000) {
        const weeks = Math.floor(seconds / 604800);
        return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
    } else if (seconds < 31536000) {
        const months = Math.floor(seconds / 2592000);
        return `${months} ${months === 1 ? 'month' : 'months'} ago`;
    } else {
        const years = Math.floor(seconds / 31536000);
        return `${years} ${years === 1 ? 'year' : 'years'} ago`;
    }
};